
import { useState, useEffect } from 'react';
import { supabase, type Game, type Result, type Standing } from '../../../lib/supabase';

interface Team {
  id: string;
  name: string;
  photo?: string;
}

/**
 * CalendarSection – displays a calendar with upcoming games,
 * a list of results and (optionally) standings.
 */
const CalendarSection = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [results, setResults] = useState<Result[]>([]);
  const [standings, setStandings] = useState<Standing[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [activeTab, setActiveTab] = useState<'calendar' | 'results'>('calendar');
  const [currentGameIndex, setCurrentGameIndex] = useState(0);

  /* -------------------------------------------------------------------------- */
  /* Data fetching                                                             */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    fetchAllTeams();
    fetchGames();
    fetchResults();
    fetchStandings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Fetch all teams from both tables */
  const fetchAllTeams = async () => {
    try {
      // Load teams from the `teams` table
      const { data: teamsData, error: teamsError } = await supabase
        .from('teams')
        .select('id, name, logo')
        .order('name', { ascending: true });

      if (teamsError) throw teamsError;

      // Load opponent teams from the `opponent_teams` table
      const { data: opponentTeamsData, error: opponentError } = await supabase
        .from('opponent_teams')
        .select('id, name, logo')
        .order('name', { ascending: true });

      if (opponentError) throw opponentError;

      // Merge both collections keeping a consistent shape
      const allTeams: Team[] = [
        ...(teamsData ?? []).map(t => ({
          id: t.id,
          name: t.name,
          // Para partidos usamos el campo logo
          photo: t.logo,
        })),
        ...(opponentTeamsData ?? []).map(t => ({
          id: t.id,
          name: t.name,
          // Para partidos usamos el campo logo
          photo: t.logo,
        })),
      ];

      setTeams(allTeams);
    } catch (error) {
      console.error('Error loading teams:', error);
    }
  };

  /** Fetch games that do *not* yet have a result attached */
  const fetchGames = async () => {
    try {
      // Obtener todos los partidos
      const { data: allGamesData, error: gamesError } = await supabase
        .from('games')
        .select('*')
        .order('date', { ascending: true });

      if (gamesError) {
        console.error('Error fetching games:', gamesError);
        setGames([]);
        return;
      }

      setGames(allGamesData ?? []);
    } catch (error) {
      console.error('Unexpected error fetching games:', error);
      setGames([]);
    }
  };

  // Función para verificar si un partido ya se ha jugado
  const isGamePlayed = (gameDate: string, gameTime: string) => {
    const now = new Date();
    const gameDateTime = new Date(`${gameDate}T${gameTime}`);
    return gameDateTime < now;
  };

  // Obtener partidos anteriores (ya jugados) sin resultado
  const getPastGames = () => {
    return games.filter(game => isGamePlayed(game.date, game.time));
  };

  /** Fetch all results ordered by creation date (newest first) */
  const fetchResults = async () => {
    try {
      const { data, error } = await supabase
        .from('results')
        .select(`
          *,
          games!inner(*)
        `)
        .order('created_at', { asc: false });

      if (error) {
        console.error('Error fetching results:', error);
        setResults([]);
        return;
      }

      // Sort by the related game's date (most recent first)
      const sortedResults = (data ?? []).sort((a, b) => {
        const dateA = new Date(a.games.date);
        const dateB = new Date(b.games.date);
        return dateB.getTime() - dateA.getTime();
      });

      setResults(sortedResults);
    } catch (error) {
      console.error('Unexpected error fetching results:', error);
      setResults([]);
    }
  };

  /** Fetch the current standings */
  const fetchStandings = async () => {
    try {
      const { data, error } = await supabase
        .from('standings')
        .select('*')
        .order('points', { asc: false });

      if (error) {
        console.error('Error fetching standings:', error);
        setStandings([]);
        return;
      }

      setStandings(data ?? []);
    } catch (error) {
      console.error('Unexpected error fetching standings:', error);
      setStandings([]);
    }
  };

  /* -------------------------------------------------------------------------- */
  /* Calendar helpers                                                          */
  /* -------------------------------------------------------------------------- */

  const monthNames = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];

  const dayNames = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

  /** Returns an array of objects representing the 6‑week grid of a month */
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    // Convert Sunday (0) to Monday‑based index (0 = Monday)
    let firstDayOfWeek = firstDay.getDay();
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

    const days: {
      date: number;
      isCurrentMonth: boolean;
      fullDate: Date;
    }[] = [];

    // Previous month filler
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const prevDate = new Date(year, month, -i);
      days.push({
        date: prevDate.getDate(),
        isCurrentMonth: false,
        fullDate: prevDate,
      });
    }

    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      const fullDate = new Date(year, month, d);
      days.push({
        date: d,
        isCurrentMonth: true,
        fullDate,
      });
    }

    // Next month filler to always have 42 cells (6 weeks)
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const nextDate = new Date(year, month + 1, i);
      days.push({
        date: i,
        isCurrentMonth: false,
        fullDate: nextDate,
      });
    }

    return days;
  };

  const hasGameOnDate = (date: Date) => {
    const dateStr = date.toLocaleDateString('en-CA'); // Formato YYYY-MM-DD
    return games.some(game => game.date === dateStr);
  };

  const getGamesForDate = (date: Date) => {
    const dateStr = date.toLocaleDateString('en-CA'); // Formato YYYY-MM-DD
    return games.filter(game => game.date === dateStr);
  };

  // Función para obtener el resultado de un partido específico
  const getResultForGame = (gameId: string) => {
    return results.find(result => result.game_id === gameId);
  };

  // Función para verificar si una fecha tiene partidos con resultados
  const hasResultOnDate = (date: Date) => {
    const dateStr = date.toLocaleDateString('en-CA');
    const gamesOnDate = games.filter(game => game.date === dateStr);
    return gamesOnDate.some(game => getResultForGame(game.id));
  };

  // Función para determinar el tipo de resultado para Viubàsquet
  const getViubasquetResult = (game: Game) => {
    const result = getResultForGame(game.id);
    if (!result) return null;

    const isViubasquetHome = game.home_team.toLowerCase().includes('viubàsquet') || game.home_team.toLowerCase().includes('viubasquet');
    const isViubasquetAway = game.away_team.toLowerCase().includes('viubàsquet') || game.away_team.toLowerCase().includes('viubasquet');
    
    if (isViubasquetHome) {
      return result.home_score > result.away_score ? 'victoria' : 'derrota';
    } else if (isViubasquetAway) {
      return result.away_score > result.home_score ? 'victoria' : 'derrota';
    }
    
    return 'neutral';
  };

  // Función para obtener los colores del día basado en múltiples resultados
  const getDayColors = (date: Date) => {
    if (!date) return { dayColor: '', indicatorColor: '' };
    
    const dateStr = date.toLocaleDateString('en-CA');
    const gamesOnDate = games.filter(game => game.date === dateStr);
    
    if (gamesOnDate.length === 0) return { dayColor: '', indicatorColor: '' };
    
    // Separar partidos con y sin resultados
    const gamesWithResults = gamesOnDate.filter(game => getResultForGame(game.id));
    const gamesWithoutResults = gamesOnDate.filter(game => !getResultForGame(game.id));
    
    // Si no hay resultados, mostrar como próximos partidos
    if (gamesWithResults.length === 0) {
      return {
        dayColor: 'bg-pink-500/30 text-white font-semibold',
        indicatorColor: 'bg-pink-300'
      };
    }
    
    // Obtener tipos de resultados únicos
    const resultTypes = gamesWithResults.map(game => getViubasquetResult(game)).filter(Boolean);
    const uniqueResults = [...new Set(resultTypes)];
    
    // Si solo hay un tipo de resultado
    if (uniqueResults.length === 1) {
      const resultType = uniqueResults[0];
      if (resultType === 'victoria') {
        return {
          dayColor: 'bg-green-500/30 text-white font-semibold',
          indicatorColor: 'bg-green-400'
        };
      } else if (resultType === 'derrota') {
        return {
          dayColor: 'bg-red-500/30 text-white font-semibold',
          indicatorColor: 'bg-red-400'
        };
      } else if (resultType === 'neutral') {
        return {
          dayColor: 'bg-blue-500/30 text-white font-semibold',
          indicatorColor: 'bg-blue-400'
        };
      }
    }
    
    // Si hay múltiples tipos de resultados, usar gradiente
    if (uniqueResults.includes('victoria') && uniqueResults.includes('derrota')) {
      return {
        dayColor: 'bg-gradient-to-r from-green-500/30 to-red-500/30 text-white font-semibold',
        indicatorColor: 'bg-gradient-to-r from-green-400 to-red-400'
      };
    } else if (uniqueResults.includes('victoria') && uniqueResults.includes('neutral')) {
      return {
        dayColor: 'bg-gradient-to-r from-green-500/30 to-blue-500/30 text-white font-semibold',
        indicatorColor: 'bg-gradient-to-r from-green-400 to-blue-400'
      };
    } else if (uniqueResults.includes('derrota') && uniqueResults.includes('neutral')) {
      return {
        dayColor: 'bg-gradient-to-r from-red-500/30 to-blue-500/30 text-white font-semibold',
        indicatorColor: 'bg-gradient-to-r from-red-400 to-blue-400'
      };
    }
    
    // Fallback para casos no contemplados
    return {
      dayColor: 'bg-blue-500/30 text-white font-semibold',
      indicatorColor: 'bg-blue-400'
    };
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + (direction === 'prev' ? -1 : 1));
      return newDate;
    });
  };

  const handleDateClick = (day: { isCurrentMonth: boolean; fullDate: Date }) => {
    if (day.isCurrentMonth) {
      setSelectedDate(day.fullDate);
      setCurrentGameIndex(0); // Reset carousel to first game
    }
  };

  const days = getDaysInMonth(currentDate);
  const selectedGames = selectedDate ? getGamesForDate(selectedDate) : [];
  const selectedGame = selectedGames[currentGameIndex] || null;

  const navigateGame = (direction: 'prev' | 'next') => {
    if (selectedGames.length <= 1) return;
    
    if (direction === 'prev') {
      setCurrentGameIndex(prev => prev === 0 ? selectedGames.length - 1 : prev - 1);
    } else {
      setCurrentGameIndex(prev => prev === selectedGames.length - 1 ? 0 : prev + 1);
    }
  };

  /* -------------------------------------------------------------------------- */
  /* Render sections                                                            */
  /* -------------------------------------------------------------------------- */

  const renderCalendar = () => (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 lg:mb-6">
        <button
          onClick={() => navigateMonth('prev')}
          className="p-2 lg:p-3 hover:bg-white/10 rounded-lg transition-colors"
          aria-label="Mes anterior"
        >
          <i className="ri-arrow-left-s-line text-lg lg:text-xl text-white/80"></i>
        </button>

        <h3 className="text-lg lg:text-2xl font-semibold text-white">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h3>

        <button
          onClick={() => navigateMonth('next')}
          className="p-2 lg:p-3 hover:bg-white/10 rounded-lg transition-colors"
          aria-label="Mes siguiente"
        >
          <i className="ri-arrow-right-s-line text-lg lg:text-xl text-white/80"></i>
        </button>
      </div>

      {/* Week day names */}
      <div className="grid grid-cols-7 gap-1 lg:gap-2 mb-2 lg:mb-4">
        {dayNames.map(d => (
          <div
            key={d}
            className="text-center text-sm lg:text-base font-medium text-white/60 py-2 lg:py-3"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1 lg:gap-2 mb-4 lg:mb-6">
        {days.map((day, idx) => {
          const hasGame = day.isCurrentMonth && hasGameOnDate(day.fullDate);
          const hasResult = day.isCurrentMonth && hasResultOnDate(day.fullDate);
          const isSelected =
            selectedDate &&
            day.fullDate.toDateString() === selectedDate.toDateString();
          const isTodayDate = day.isCurrentMonth && isToday(day.fullDate);

          // Obtener colores del día
          const { dayColor, indicatorColor } = getDayColors(day.fullDate);

          return (
            <button
              key={idx}
              onClick={() => handleDateClick(day)}
              className={`
                aspect-square flex items-center justify-center text-sm lg:text-base relative
                ${day.isCurrentMonth ? 'text-white/90 hover:bg-white/10' : 'text-white/30'}
                ${isSelected ? 'bg-pink-500 text-white' : ''}
                ${!isSelected && dayColor ? dayColor : ''}
                ${isTodayDate && !isSelected && !hasGame && !hasResult ? 'bg-white/20 text-white' : ''}
                rounded-lg transition-colors
              `}
            >
              {day.date}
              {(hasGame || hasResult) && !isSelected && (
                <div className={`absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 lg:w-1.5 lg:h-1.5 ${indicatorColor || 'bg-pink-300'} rounded-full`}></div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected game details with carousel */}
      {selectedGame && (
        <>
          <div className="bg-white/5 backdrop-blur-sm rounded-lg p-3 lg:p-4 mb-4 lg:mb-6 border border-white/10">
            <div className="flex items-center justify-center gap-2 text-sm lg:text-base text-white/90 mb-2 lg:mb-3">
              {selectedGame.jornada && (
                <>
                  <span className="bg-pink-500/80 text-white px-2 py-0.5 lg:px-3 lg:py-1 rounded-full text-xs lg:text-sm font-medium">
                    Jornada {selectedGame.jornada}
                  </span>
                  <span className="text-white/50">•</span>
                </>
              )}
              <i className="ri-calendar-line"></i>
              <span>{new Date(selectedGame.date).toLocaleDateString('es-ES', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric' 
              })}</span>
              <span className="text-white/50">•</span>
              <i className="ri-time-line"></i>
              <span>{selectedGame.time}</span>
            </div>

            {/* Carousel indicators */}
            {selectedGames.length > 1 && (
              <div className="flex items-center justify-center gap-2 mt-2 lg:mt-3">
                <button
                  onClick={() => navigateGame('prev')}
                  className="p-1 lg:p-2 hover:bg-white/10 rounded transition-colors"
                >
                  <i className="ri-arrow-left-s-line text-white/70"></i>
                </button>
                <div className="flex gap-1 lg:gap-2">
                  {selectedGames.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 lg:w-3 lg:h-3 rounded-full transition-colors ${
                        index === currentGameIndex ? 'bg-white' : 'bg-white/30'
                      }`}
                    />
                  ))}
                </div>
                <button
                  onClick={() => navigateGame('next')}
                  className="p-1 lg:p-2 hover:bg-white/10 rounded transition-colors"
                >
                  <i className="ri-arrow-right-s-line text-white/70"></i>
                </button>
              </div>
            )}
          </div>

          {/* Sección unificada del partido con resultado o sin resultado */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 lg:p-6 mb-4 lg:mb-6 border border-white/20">
            {(() => {
              const gameResult = getResultForGame(selectedGame.id);
              
              if (gameResult) {
                // Mostrar partido con resultado
                const resultType = getViubasquetResult(selectedGame);
                let resultColor = 'bg-gray-500/80';
                let resultText = 'Empate';
                
                if (resultType === 'victoria') {
                  resultColor = 'bg-green-500/80';
                  resultText = 'Victoria';
                } else if (resultType === 'derrota') {
                  resultColor = 'bg-red-500/80';
                  resultText = 'Derrota';
                } else if (resultType === 'neutral') {
                  resultColor = 'bg-blue-500/80';
                  resultText = 'Finalizado';
                }

                return (
                  <>
                    <div className="text-center mb-3 lg:mb-4">
                      <span className={`inline-block px-3 py-1 lg:px-4 lg:py-2 rounded-full text-sm lg:text-base font-medium text-white ${resultColor}`}>
                        {resultText}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-center gap-4 lg:gap-8">
                      <div className="flex-1 text-center">
                        <div className="w-16 h-16 lg:w-24 lg:h-24 mx-auto mb-2 lg:mb-3 rounded-xl flex items-center justify-center">
                          <img
                            src={selectedGame.home_team_logo || `https://yfsgqrzophdjgqardzvy.supabase.co/storage/v1/object/public/media/${selectedGame.home_team?.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}.png`}
                            alt={selectedGame.home_team}
                            className="w-12 h-12 lg:w-20 lg:h-20 object-contain"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = 'https://readdy.ai/api/search-image?query=Basketball%20team%20logo%2C%20circular%20emblem%2C%20orange%20and%20white%20colors%2C%20modern%20design%2C%20sports%20logo%2C%20clean%20background%2C%20professional%20basketball%20club%20badge&width=60&height=60&seq=home-logo&orientation=squarish';
                            }}
                          />
                        </div>
                        <p className="text-sm lg:text-base font-semibold text-white line-clamp-2 mb-1 lg:mb-2">
                          {selectedGame.home_team || 'Local'}
                        </p>
                        <span className="text-2xl lg:text-4xl font-bold text-white">{gameResult.home_score}</span>
                      </div>

                      <div className="flex items-center justify-center flex-shrink-0">
                        <span className="text-lg lg:text-2xl font-bold text-white/60">-</span>
                      </div>

                      <div className="flex-1 text-center">
                        <div className="w-16 h-16 lg:w-24 lg:h-24 mx-auto mb-2 lg:mb-3 rounded-xl flex items-center justify-center">
                          <img
                            src={selectedGame.away_team_logo || `https://yfsgqrzophdjgqardzvy.supabase.co/storage/v1/object/public/media/${selectedGame.away_team?.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}.png`}
                            alt={selectedGame.away_team}
                            className="w-12 h-12 lg:w-20 lg:h-20 object-contain"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = 'https://readdy.ai/api/search-image?query=Basketball%20team%20logo%2C%20circular%20emblem%2C%20blue%20and%20white%20colors%2C%20modern%20design%2C%20sports%20logo%2C%20clean%20background%2C%20professional%20basketball%20club%20badge&width=60&height=60&seq=away-logo&orientation=squarish';
                            }}
                          />
                        </div>
                        <p className="text-sm lg:text-base font-semibold text-white line-clamp-2 mb-1 lg:mb-2">
                          {selectedGame.away_team || 'Visitante'}
                        </p>
                        <span className="text-2xl lg:text-4xl font-bold text-white">{gameResult.away_score}</span>
                      </div>
                    </div>
                  </>
                );
              } else {
                // Mostrar partido próximo sin resultado
                return (
                  <div className="flex items-center justify-center gap-4 lg:gap-8">
                    <div className="flex-1 text-center">
                      <div className="w-16 h-16 lg:w-24 lg:h-24 mx-auto mb-2 lg:mb-3 rounded-xl flex items-center justify-center">
                        <img
                          src={selectedGame.home_team_logo || `https://yfsgqrzophdjgqardzvy.supabase.co/storage/v1/object/public/media/${selectedGame.home_team?.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}.png`}
                          alt={selectedGame.home_team}
                          className="w-12 h-12 lg:w-20 lg:h-20 object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'https://readdy.ai/api/search-image?query=Basketball%20team%20logo%2C%20circular%20emblem%2C%20orange%20and%20white%20colors%2C%20modern%20design%2C%20sports%20logo%2C%20clean%20background%2C%20professional%20basketball%20club%20badge&width=60&height=60&seq=home-logo&orientation=squarish';
                          }}
                        />
                      </div>
                      <p className="text-sm lg:text-base font-semibold text-white line-clamp-2">
                        {selectedGame.home_team || 'Local'}
                      </p>
                    </div>

                    <div className="flex items-center justify-center flex-shrink-0">
                      <span className="text-lg lg:text-2xl font-bold text-white">VS</span>
                    </div>

                    <div className="flex-1 text-center">
                      <div className="w-16 h-16 lg:w-24 lg:h-24 mx-auto mb-2 lg:mb-3 rounded-xl flex items-center justify-center">
                        <img
                          src={selectedGame.away_team_logo || `https://yfsgqrzophdjgqardzvy.supabase.co/storage/v1/object/public/media/${selectedGame.away_team?.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}.png`}
                          alt={selectedGame.away_team}
                          className="w-12 h-12 lg:w-20 lg:h-20 object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'https://readdy.ai/api/search-image?query=Basketball%20team%20logo%2C%20circular%20emblem%2C%20blue%20and%20white%20colors%2C%20modern%20design%2C%20sports%20logo%2C%20clean%20background%2C%20professional%20basketball%20club%20badge&width=60&height=60&seq=away-logo&orientation=squarish';
                          }}
                        />
                      </div>
                      <p className="text-sm lg:text-base font-semibold text-white line-clamp-2">
                        {selectedGame.away_team || 'Visitante'}
                      </p>
                    </div>
                  </div>
                );
              }
            })()}

            {selectedGame.location && (
              <div className="flex items-center justify-center text-sm lg:text-base text-white/90 mt-4 lg:mt-6 pt-3 lg:pt-4 border-t border-white/10">
                <i className="ri-map-pin-line mr-2"></i>
                <span>{selectedGame.location}</span>
              </div>
            )}
          </div>
        </>
      )}

      {/* Leyenda de colores */}
      <div className="mt-6 lg:mt-8 bg-white/5 backdrop-blur-sm rounded-lg p-3 lg:p-4 border border-white/10">
        <h4 className="text-sm lg:text-base font-medium text-white mb-2 lg:mb-3">Leyenda:</h4>
        <div className="grid grid-cols-2 gap-2 lg:gap-3 text-xs lg:text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 lg:w-4 lg:h-4 bg-green-500/60 rounded"></div>
            <span className="text-white/80">Victoria</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 lg:w-4 lg:h-4 bg-red-500/60 rounded"></div>
            <span className="text-white/80">Derrota</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 lg:w-4 lg:h-4 bg-pink-500/60 rounded"></div>
            <span className="text-white/80">Próximo partido</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 lg:w-4 lg:h-4 bg-gradient-to-r from-green-500/60 to-red-500/60 rounded"></div>
            <span className="text-white/80">Resultados mixtos</span>
          </div>
        </div>
      </div>
    </div>
  );

  /** Render the results section – includes win/lose badge */
  const renderResults = () => (
    <div>
      <h4 className="font-semibold text-white mb-4 lg:mb-6 text-base lg:text-xl">Resultados</h4>
      {results.length === 0 ? (
        <div className="text-center py-8 lg:py-12">
          <i className="ri-trophy-line text-4xl lg:text-6xl text-white/30 mb-2 lg:mb-4"></i>
          <p className="text-white/60 text-sm lg:text-base">No hay resultados disponibles</p>
        </div>
      ) : (
        <div className="space-y-3 lg:space-y-4">
          {results.map(result => {
            // Determinar si Viubàsquet ganó o perdió
            const isViubasquetHome = result.home_team.toLowerCase().includes('viubàsquet') || result.home_team.toLowerCase().includes('viubasquet');
            const isViubasquetAway = result.away_team.toLowerCase().includes('viubàsquet') || result.away_team.toLowerCase().includes('viubasquet');
            
            let resultType = 'Finalizado';
            let resultColor = 'bg-blue-500/80 text-white';
            
            if (isViubasquetHome) {
              // Viubàsquet juega en casa
              if (result.home_score > result.away_score) {
                resultType = 'Victoria';
                resultColor = 'bg-green-500/80 text-white';
              } else {
                resultType = 'Derrota';
                resultColor = 'bg-red-500/80 text-white';
              }
            } else if (isViubasquetAway) {
              // Viubàsquet juega fuera
              if (result.away_score > result.home_score) {
                resultType = 'Victoria';
                resultColor = 'bg-green-500/80 text-white';
              } else {
                resultType = 'Derrota';
                resultColor = 'bg-red-500/80 text-white';
              }
            } else {
              // Partido sin Viubàsquet - mostrar resultado genérico
              if (result.home_score > result.away_score) {
                resultType = 'Local';
                resultColor = 'bg-blue-500/80 text-white';
              } else {
                resultType = 'Visitante';
                resultColor = 'bg-purple-500/80 text-white';
              }
            }

            return (
              <div
                key={result.id}
                className="bg-white/10 backdrop-blur-sm rounded-lg p-4 lg:p-6 border border-white/20"
              >
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    {/* Jornada y fecha */}
                    <div className="flex items-center gap-2 mb-2 lg:mb-3">
                      {result.games.jornada && (
                        <span className="text-xs lg:text-sm bg-pink-500/80 text-white px-2 py-0.5 lg:px-3 lg:py-1 rounded-full">
                          Jornada {result.games.jornada}
                        </span>
                      )}
                      <div className="text-xs lg:text-sm text-white/60">
                        {new Date(result.games.date).toLocaleDateString('es-ES', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric' 
                        })} • {result.games.time}
                      </div>
                    </div>

                    {/* Home side */}
                    <div className="flex items-center justify-between mb-2 lg:mb-3">
                      <div className="flex items-center">
                        <img
                          src={result.games.home_team_logo || `https://yfsgqrzophdjgqardzvy.supabase.co/storage/v1/object/public/media/${result.home_team?.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}.png`}
                          alt={result.home_team}
                          className="w-8 h-8 lg:w-12 lg:h-12 object-contain mr-3 lg:mr-4"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'https://readdy.ai/api/search-image?query=Basketball%20team%20logo%2C%20circular%20emblem%2C%20orange%20and%20white%20colors%2C%20modern%20design%2C%20sports%20logo%2C%20clean%20background%2C%20professional%20basketball%20club%20badge&width=60&height=60&seq=home-logo&orientation=squarish';
                          }}
                        />
                        <span className="text-sm lg:text-base font-medium text-white">{result.home_team}</span>
                      </div>
                      <span className="text-lg lg:text-2xl font-bold text-white">{result.home_score}</span>
                    </div>

                    {/* Away side */}
                    <div className="flex items-center justify-between mb-2 lg:mb-3">
                      <div className="flex items-center">
                        <img
                          src={result.games.away_team_logo || `https://yfsgqrzophdjgqardzvy.supabase.co/storage/v1/object/public/media/${result.away_team?.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}.png`}
                          alt={result.away_team}
                          className="w-8 h-8 lg:w-12 lg:h-12 object-contain mr-3 lg:mr-4"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'https://readdy.ai/api/search-image?query=Basketball%20team%20logo%2C%20circular%20emblem%2C%20blue%20and%20white%20colors%2C%20modern%20design%2C%20sports%20logo%2C%20clean%20background%2C%20professional%20basketball%20club%20badge&width=60&height=60&seq=away-logo&orientation=squarish';
                          }}
                        />
                        <span className="text-sm lg:text-base font-medium text-white">{result.away_team}</span>
                      </div>
                      <span className="text-lg lg:text-2xl font-bold text-white">{result.away_score}</span>
                    </div>
                  </div>

                  {/* Result badge */}
                  <div className="ml-4 lg:ml-6 text-center">
                    <div className={`px-2 py-1 lg:px-3 lg:py-2 rounded-full text-xs lg:text-sm font-medium ${resultColor}`}>
                      {resultType}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  /** Render the standings table – currently hidden behind a commented tab */
  const renderStandings = () => (
    <div>
      <h4 className="font-semibold text-white mb-4 lg:mb-6 text-base lg:text-xl">Clasificación</h4>
      {standings.length === 0 ? (
        <div className="text-center py-8 lg:py-12">
          <i className="ri-bar-chart-line text-4xl lg:text-6xl text-white/30 mb-2 lg:mb-4"></i>
          <p className="text-white/60 text-sm lg:text-base">No hay clasificación disponible</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm lg:text-base">
            <thead>
              <tr className="border-b border-white/20">
                <th className="text-left py-2 lg:py-3 px-1 lg:px-2 font-medium text-white/70">#</th>
                <th className="text-left py-2 lg:py-3 px-1 lg:px-2 font-medium text-white/70">Equipo</th>
                <th className="text-center py-2 lg:py-3 px-1 lg:px-2 font-medium text-white/70">PJ</th>
                <th className="text-center py-2 lg:py-3 px-1 lg:px-2 font-medium text-white/70">G</th>
                <th className="text-center py-2 lg:py-3 px-1 lg:px-2 font-medium text-white/70">P</th>
                <th className="text-center py-2 lg:py-3 px-1 lg:px-2 font-medium text-white/70">Pts</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((team, idx) => (
                <tr key={team.id} className="border-b border-white/10">
                  <td className="py-2 lg:py-3 px-1 lg:px-2 font-medium text-white">{idx + 1}</td>
                  <td className="py-2 lg:py-3 px-1 lg:px-2 text-white truncate">{team.team_name}</td>
                  <td className="py-2 lg:py-3 px-1 lg:px-2 text-center text-white/70">{team.games_played}</td>
                  <td className="py-2 lg:py-3 px-1 lg:px-2 text-center text-green-400">{team.wins}</td>
                  <td className="py-2 lg:py-3 px-1 lg:px-2 text-center text-red-400">{team.losses}</td>
                  <td className="py-2 lg:py-3 px-1 lg:px-2 text-center font-semibold text-white">{team.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  /* -------------------------------------------------------------------------- */
  /* Main render                                                                */
  /* -------------------------------------------------------------------------- */

  return (
    <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
      <div className="px-4 lg:px-8 py-6 lg:py-8">
        <div className="max-w-4xl mx-auto" style={{ maxWidth: '40rem' }}>
          {/* Tabs */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 lg:p-6 border border-white/20 mb-6 lg:mb-8">
            <div className="flex bg-white/10 rounded-lg p-1 mb-4 lg:mb-6">
              <button
                onClick={() => setActiveTab('calendar')}
                className={`flex-1 py-2 lg:py-3 px-3 lg:px-4 rounded-md text-sm lg:text-base font-medium transition-colors ${
                  activeTab === 'calendar'
                    ? 'bg-white/20 text-white shadow-sm'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                <i className="ri-calendar-line mr-1 lg:mr-2"></i>
                Calendario
              </button>
              <button
                onClick={() => setActiveTab('results')}
                className={`flex-1 py-2 lg:py-3 px-3 lg:px-4 rounded-md text-sm lg:text-base font-medium transition-colors ${
                  activeTab === 'results'
                    ? 'bg-white/20 text-white shadow-sm'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                <i className="ri-trophy-line mr-1 lg:mr-2"></i>
                Resultados
              </button>
            </div>

            {/* Content */}
            {activeTab === 'calendar' && renderCalendar()}
            {activeTab === 'results' && renderResults()}
            {/* Uncomment the line below if you want to expose the standings tab in the future */}
            {/* {activeTab === 'standings' && renderStandings()} */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CalendarSection;
